import { Fragment, useState, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Layers,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Pagination } from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { categoryLabel } from "@/lib/serviceCategory";
import {
  platformApi,
  type PlatformService,
  type ServiceCreateRequest,
  type ServiceUpdateRequest,
  type ServiceFeature,
  type ServiceFeatureCreateRequest,
  type ServiceFeatureUpdateRequest,
  type ServiceCategory,
} from "@/services/api/platform";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePaginatedData, type PaginatedResponse } from "@/hooks/usePaginatedData";

export const Route = createFileRoute("/portal/platform/products")({
  component: ProductsPage,
});

function ProductsPage() {
  const { token } = useAuth();
  const [showInactive, setShowInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("sort_order");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [editingService, setEditingService] = useState<PlatformService | null>(
    null,
  );
  const [deleteService, setDeleteService] = useState<PlatformService | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState<ServiceCreateRequest>({
    name: "",
    display_name: "",
    description: "",
    pricing_model: "tier",
    category: "platform" as const,
    is_active: true,
    is_launched: false,
    has_free_tier: false,
    trial_days: 0,
    sort_order: 0,
  });

  // Features management (for all services)
  const [featuresServiceId, setFeaturesServiceId] = useState<string | null>(null);
  const [features, setFeatures] = useState<ServiceFeature[]>([]);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [showCreateFeatureDialog, setShowCreateFeatureDialog] = useState(false);
  const [editingFeature, setEditingFeature] = useState<ServiceFeature | null>(null);
  const [createFeatureForm, setCreateFeatureForm] = useState<ServiceFeatureCreateRequest>({
    service_id: "",
    feature_key: "",
    display_name: "",
    description: "",
    sku_code: "",
    price_monthly_zar: 0,
    price_monthly_usd: 0,
    is_base: false,
    is_active: true,
    sort_order: 0,
  });

  // Server-side paginated data
  const fetchServices = useCallback(
    async (page: number, pageSize: number): Promise<PaginatedResponse<PlatformService>> => {
      if (!token) return { data: [], totalCount: 0, page, pageSize };
      const offset = (page - 1) * pageSize;
      const result = await platformApi.listPlatformServicesPaginated(token, {
        include_inactive: showInactive,
        search: searchTerm || undefined,
        offset,
        limit: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      return {
        data: result.items,
        totalCount: result.total,
        page,
        pageSize,
      };
    },
    [token, showInactive, searchTerm, sortBy, sortOrder],
  );

  const {
    data: services,
    isLoading,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<PlatformService>({
    fetchFn: fetchServices,
    dependencies: [token, showInactive, searchTerm, sortBy, sortOrder],
  });

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  }
  function getSortDirection(column: string): "asc" | "desc" | null {
    return sortBy === column ? sortOrder : null;
  }

  async function handleCreateService() {
    if (!token || !createForm.name || !createForm.display_name) return;
    setIsSaving(true);
    try {
      await platformApi.createPlatformService(token, createForm);
      toast.success(`Created ${createForm.display_name}`);
      setShowCreateDialog(false);
      setCreateForm({
        name: "",
        display_name: "",
        description: "",
        pricing_model: "tier",
        category: "platform" as const,
        is_active: true,
        is_launched: false,
        has_free_tier: false,
        trial_days: 0,
        sort_order: 0,
      });
      refresh();
    } catch (error) {
      console.error("Failed to create service:", error);
      toast.error("Failed to create product. Name may already exist.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSaveService(service: PlatformService) {
    if (!token) return;
    setIsSaving(true);
    try {
      const updateData: ServiceUpdateRequest = {
        display_name: service.display_name,
        description: service.description || undefined,
        category: service.category,
        has_free_tier: service.has_free_tier,
        trial_days: service.trial_days,
        is_active: service.is_active,
        is_launched: service.is_launched,
        sort_order: service.sort_order,
      };
      await platformApi.updatePlatformService(token, service.id, updateData);
      toast.success(`Updated ${service.display_name}`);
      setEditingService(null);
      refresh();
    } catch (error) {
      console.error("Failed to update service:", error);
      toast.error("Failed to update service");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteService(service: PlatformService) {
    if (!token) return;
    try {
      await platformApi.deletePlatformService(token, service.id);
      toast.success(`Deactivated ${service.display_name}`);
      setDeleteService(null);
      refresh();
    } catch (error) {
      console.error("Failed to delete service:", error);
      toast.error("Failed to deactivate service");
    }
  }

  async function handleToggleField(
    service: PlatformService,
    field: "is_active" | "is_launched" | "has_free_tier",
    value: boolean,
  ) {
    if (!token) return;
    try {
      await platformApi.updatePlatformService(token, service.id, {
        [field]: value,
      });
      refresh();
    } catch (error) {
      console.error(`Failed to update ${field}:`, error);
      toast.error(`Failed to update ${service.display_name}`);
    }
  }

  // --- Feature management ---

  async function toggleFeatures(serviceId: string) {
    if (featuresServiceId === serviceId) {
      setFeaturesServiceId(null);
      return;
    }
    setFeaturesServiceId(serviceId);
    await loadFeatures(serviceId);
  }

  async function loadFeatures(serviceId: string) {
    if (!token) return;
    setFeaturesLoading(true);
    try {
      const result = await platformApi.listServiceFeatures(token, serviceId, true);
      setFeatures(result.features);
    } catch (error) {
      console.error("Failed to load features:", error);
      toast.error("Failed to load features");
    } finally {
      setFeaturesLoading(false);
    }
  }

  async function handleCreateFeature(serviceId: string) {
    if (!token || !createFeatureForm.feature_key || !createFeatureForm.display_name) return;
    setIsSaving(true);
    try {
      await platformApi.createServiceFeature(token, {
        ...createFeatureForm,
        service_id: serviceId,
      });
      toast.success(`Created feature: ${createFeatureForm.display_name}`);
      setShowCreateFeatureDialog(false);
      setCreateFeatureForm({
        service_id: "",
        feature_key: "",
        display_name: "",
        description: "",
        sku_code: "",
        price_monthly_zar: 0,
        price_monthly_usd: 0,
        is_base: false,
        is_active: true,
        sort_order: 0,
      });
      await loadFeatures(serviceId);
    } catch (error) {
      console.error("Failed to create feature:", error);
      toast.error("Failed to create feature");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSaveFeature(feature: ServiceFeature) {
    if (!token) return;
    setIsSaving(true);
    try {
      const update: ServiceFeatureUpdateRequest = {
        display_name: feature.display_name,
        description: feature.description ?? undefined,
        sku_code: feature.sku_code ?? undefined,
        price_monthly_zar: feature.price_monthly_zar,
        price_monthly_usd: feature.price_monthly_usd,
        is_base: feature.is_base,
        is_active: feature.is_active,
        sort_order: feature.sort_order,
      };
      await platformApi.updateServiceFeature(token, feature.id, update);
      toast.success(`Updated feature: ${feature.display_name}`);
      setEditingFeature(null);
      if (featuresServiceId) await loadFeatures(featuresServiceId);
    } catch (error) {
      console.error("Failed to update feature:", error);
      toast.error("Failed to update feature");
    } finally {
      setIsSaving(false);
    }
  }

  function formatPrice(cents: number, currency: "usd" | "zar"): string {
    const amount = cents / 100;
    return currency === "usd"
      ? `$${amount.toLocaleString()}`
      : `R${amount.toLocaleString()}`;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Layers className="h-6 w-6" />
            Platform Products (Admin)
          </h1>
          <p className="text-muted-foreground">
            Product catalog with pricing and tiers (platform-wide, not
            org-specific)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search products..."
            containerClassName="w-[250px]"
          />
          <div className="flex items-center space-x-2">
            <Switch
              id="show-inactive"
              checked={showInactive}
              onCheckedChange={setShowInactive}
            />
            <Label htmlFor="show-inactive">Show inactive</Label>
          </div>
          <Button variant="outline" onClick={() => refresh()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Products</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pagination.totalCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {services.filter((s) => s.is_active).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">With Free Tier</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {services.filter((s) => s.has_free_tier).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Services Table */}
      <Card>
        <CardHeader>
          <CardTitle>Products</CardTitle>
          <CardDescription>
            Configure available products and their pricing tiers
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No products found. Click "Add Product" to create one.</p>
            </div>
          ) : (
            <>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead direction={getSortDirection("display_name")} onSort={() => handleSort("display_name")}>Product</SortableTableHead>
                  <SortableTableHead direction={null} onSort={() => {}} disabled>Category</SortableTableHead>
                  <SortableTableHead direction={null} onSort={() => {}} disabled>Model</SortableTableHead>
                  <SortableTableHead direction={getSortDirection("is_active")} onSort={() => handleSort("is_active")}>Status</SortableTableHead>
                  <SortableTableHead direction={getSortDirection("is_launched")} onSort={() => handleSort("is_launched")}>Launched</SortableTableHead>
                  <SortableTableHead direction={getSortDirection("has_free_tier")} onSort={() => handleSort("has_free_tier")}>Free Tier</SortableTableHead>
                  <SortableTableHead direction={getSortDirection("trial_days")} onSort={() => handleSort("trial_days")}>Trial</SortableTableHead>
                  <SortableTableHead direction={null} onSort={() => {}} disabled>Pricing</SortableTableHead>
                  <SortableTableHead direction={null} onSort={() => {}} disabled className="text-right">Actions</SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {services.map((service) => {
                  return (
                  <Fragment key={service.id}>
                  <TableRow>
                    <TableCell>
                      <div>
                        <div className="font-medium">{service.display_name}</div>
                        <div className="text-sm text-muted-foreground">
                          {service.name}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={service.category === 'connector' ? 'outline' : 'secondary'}>
                        {categoryLabel(service.category)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">Tier</Badge>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={service.is_active}
                        onCheckedChange={(checked) =>
                          handleToggleField(service, "is_active", checked)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={service.is_launched}
                        onCheckedChange={(checked) =>
                          handleToggleField(service, "is_launched", checked)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={service.has_free_tier}
                        onCheckedChange={(checked) =>
                          handleToggleField(service, "has_free_tier", checked)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {service.trial_days > 0 ? (
                        <Badge variant="outline">{service.trial_days} days</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {service.pricing?.starter ? (
                        <div className="text-sm">
                          <div>{formatPrice(service.pricing.starter.monthly_usd, "usd")}/mo</div>
                          <div className="text-muted-foreground text-xs">
                            {formatPrice(service.pricing.starter.monthly_zar, "zar")}
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleFeatures(service.id)}
                        title="Manage features"
                      >
                        <Layers className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingService({ ...service })}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteService(service)}
                        disabled={!service.is_active}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  {/* Expanded features sub-row */}
                  {featuresServiceId === service.id && (
                    <TableRow>
                      <TableCell colSpan={9} className="bg-blue-50/30 p-4">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium flex items-center gap-2">
                              <Layers className="h-4 w-4" />
                              Features for {service.display_name}
                            </h4>
                            <Button
                              size="sm"
                              onClick={() => {
                                setCreateFeatureForm((f) => ({ ...f, service_id: service.id }));
                                setShowCreateFeatureDialog(true);
                              }}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add Feature
                            </Button>
                          </div>
                          {featuresLoading ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            </div>
                          ) : features.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-2">
                              No features configured. Add one to define SKU pricing.
                            </p>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <SortableTableHead direction={null} onSort={() => {}} disabled>Key</SortableTableHead>
                                  <SortableTableHead direction={null} onSort={() => {}} disabled>Display Name</SortableTableHead>
                                  <SortableTableHead direction={null} onSort={() => {}} disabled>SKU</SortableTableHead>
                                  <SortableTableHead direction={null} onSort={() => {}} disabled>ZAR/mo</SortableTableHead>
                                  <SortableTableHead direction={null} onSort={() => {}} disabled>USD/mo</SortableTableHead>
                                  <SortableTableHead direction={null} onSort={() => {}} disabled>Base</SortableTableHead>
                                  <SortableTableHead direction={null} onSort={() => {}} disabled>Active</SortableTableHead>
                                  <SortableTableHead direction={null} onSort={() => {}} disabled className="text-right">Actions</SortableTableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {features.map((feat) => (
                                  <TableRow key={feat.id}>
                                    <TableCell>
                                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{feat.feature_key}</code>
                                    </TableCell>
                                    <TableCell>
                                      <div>
                                        <div className="font-medium text-sm">{feat.display_name}</div>
                                        {feat.description && (
                                          <div className="text-xs text-muted-foreground">{feat.description}</div>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-sm">
                                      {feat.sku_code || <span className="text-muted-foreground">-</span>}
                                    </TableCell>
                                    <TableCell className="text-sm">
                                      {formatPrice(feat.price_monthly_zar, "zar")}
                                    </TableCell>
                                    <TableCell className="text-sm">
                                      {formatPrice(feat.price_monthly_usd, "usd")}
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant={feat.is_base ? "default" : "secondary"}>
                                        {feat.is_base ? "Base" : "Add-on"}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant={feat.is_active ? "default" : "secondary"}>
                                        {feat.is_active ? "Active" : "Inactive"}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setEditingFeature({ ...feat })}
                                      >
                                        <Pencil className="h-3 w-3" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  </Fragment>
                  );
                })}
              </TableBody>
            </Table>
            {pagination.totalCount > 0 && (
              <div className="mt-4">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  pageSize={pagination.pageSize}
                  pageSizeOptions={pageSizeOptions}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  canGoPrev={canGoPrev}
                  canGoNext={canGoNext}
                  onPageChange={goToPage}
                  onPageSizeChange={setPageSize}
                  onFirstPage={firstPage}
                  onLastPage={lastPage}
                  onPrevPage={prevPage}
                  onNextPage={nextPage}
                />
              </div>
            )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog
        open={editingService !== null}
        onOpenChange={(open) => !open && setEditingService(null)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
            <DialogDescription>
              Update product configuration and settings
            </DialogDescription>
          </DialogHeader>
          {editingService && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name (readonly)</Label>
                <Input value={editingService.name} disabled />
              </div>
              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input
                  value={editingService.display_name}
                  onChange={(e) =>
                    setEditingService({
                      ...editingService,
                      display_name: e.target.value,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingService.description || ""}
                  onChange={(e) =>
                    setEditingService({
                      ...editingService,
                      description: e.target.value,
                    })
                  }
                  rows={3}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Free Tier</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow free access with limited features
                  </p>
                </div>
                <Switch
                  checked={editingService.has_free_tier}
                  onCheckedChange={(checked) =>
                    setEditingService({
                      ...editingService,
                      has_free_tier: checked,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Trial Days</Label>
                <Input
                  type="number"
                  value={editingService.trial_days}
                  onChange={(e) =>
                    setEditingService({
                      ...editingService,
                      trial_days: parseInt(e.target.value) || 0,
                    })
                  }
                  min={0}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Active</Label>
                  <p className="text-sm text-muted-foreground">
                    Product is available for subscription
                  </p>
                </div>
                <Switch
                  checked={editingService.is_active}
                  onCheckedChange={(checked) =>
                    setEditingService({
                      ...editingService,
                      is_active: checked,
                    })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Launched</Label>
                  <p className="text-sm text-muted-foreground">
                    Product is live. Unlaunched products show as &ldquo;Coming
                    soon&rdquo;
                  </p>
                </div>
                <Switch
                  checked={editingService.is_launched}
                  onCheckedChange={(checked) =>
                    setEditingService({
                      ...editingService,
                      is_launched: checked,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Sort Order</Label>
                <Input
                  type="number"
                  value={editingService.sort_order}
                  onChange={(e) =>
                    setEditingService({
                      ...editingService,
                      sort_order: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingService(null)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={() => editingService && handleSaveService(editingService)}
              disabled={isSaving}
            >
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog
        open={showCreateDialog}
        onOpenChange={(open) => !open && setShowCreateDialog(false)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Product</DialogTitle>
            <DialogDescription>
              Create a new platform product. Add pricing tiers afterwards on the
              Tiers page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={createForm.name}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    name: e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9_]/g, "_"),
                  })
                }
                placeholder="e.g. reporting, crm"
              />
              <p className="text-xs text-muted-foreground">
                Lowercase identifier. Cannot be changed later.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={createForm.category || "platform"}
                onValueChange={(value: ServiceCategory) =>
                  setCreateForm({ ...createForm, category: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="platform">Platform Service</SelectItem>
                  <SelectItem value="connector">Connector Service</SelectItem>
                  <SelectItem value="managed_service">Managed service</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Platform = standard tier pricing. Connector = per-instance B2C connector.
                Managed service = a Larasoft-operated service (e.g. Managed database).
              </p>
            </div>
            <div className="space-y-2">
              <Label>Display Name</Label>
              <Input
                value={createForm.display_name}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    display_name: e.target.value,
                  })
                }
                placeholder="e.g. Reporting, CRM"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={createForm.description || ""}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    description: e.target.value,
                  })
                }
                rows={3}
                placeholder="Brief description of the product"
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Active</Label>
                <p className="text-sm text-muted-foreground">
                  Product is visible in the catalog
                </p>
              </div>
              <Switch
                checked={createForm.is_active ?? true}
                onCheckedChange={(checked) =>
                  setCreateForm({ ...createForm, is_active: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Launched</Label>
                <p className="text-sm text-muted-foreground">
                  Product is live. Unlaunched products show as &ldquo;Coming
                  soon&rdquo;
                </p>
              </div>
              <Switch
                checked={createForm.is_launched ?? false}
                onCheckedChange={(checked) =>
                  setCreateForm({ ...createForm, is_launched: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Free Tier</Label>
                <p className="text-sm text-muted-foreground">
                  Allow free access with limited features
                </p>
              </div>
              <Switch
                checked={createForm.has_free_tier ?? false}
                onCheckedChange={(checked) =>
                  setCreateForm({ ...createForm, has_free_tier: checked })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Trial Days</Label>
              <Input
                type="number"
                value={createForm.trial_days ?? 0}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    trial_days: parseInt(e.target.value) || 0,
                  })
                }
                min={0}
              />
            </div>
            <div className="space-y-2">
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={createForm.sort_order ?? 0}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    sort_order: parseInt(e.target.value) || 0,
                  })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateService}
              disabled={isSaving || !createForm.name || !createForm.display_name}
            >
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteService !== null}
        onOpenChange={(open) => !open && setDeleteService(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Product?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate {deleteService?.display_name}. Existing
              subscriptions will continue to work, but new subscriptions will not
              be available.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteService && handleDeleteService(deleteService)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Feature Dialog */}
      <Dialog open={showCreateFeatureDialog} onOpenChange={setShowCreateFeatureDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Feature</DialogTitle>
            <DialogDescription>
              Create a new feature/SKU for this service.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Feature Key *</Label>
                <Input
                  placeholder="e.g. pdf_export"
                  value={createFeatureForm.feature_key}
                  onChange={(e) => setCreateFeatureForm({ ...createFeatureForm, feature_key: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Display Name *</Label>
                <Input
                  placeholder="e.g. PDF Export"
                  value={createFeatureForm.display_name}
                  onChange={(e) => setCreateFeatureForm({ ...createFeatureForm, display_name: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Feature description..."
                value={createFeatureForm.description || ""}
                onChange={(e) => setCreateFeatureForm({ ...createFeatureForm, description: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>SKU Code</Label>
                <Input
                  placeholder="SKU-001"
                  value={createFeatureForm.sku_code || ""}
                  onChange={(e) => setCreateFeatureForm({ ...createFeatureForm, sku_code: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Price ZAR (cents)</Label>
                <Input
                  type="number"
                  value={createFeatureForm.price_monthly_zar}
                  onChange={(e) => setCreateFeatureForm({ ...createFeatureForm, price_monthly_zar: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Price USD (cents)</Label>
                <Input
                  type="number"
                  value={createFeatureForm.price_monthly_usd}
                  onChange={(e) => setCreateFeatureForm({ ...createFeatureForm, price_monthly_usd: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={createFeatureForm.is_base}
                  onCheckedChange={(checked) => setCreateFeatureForm({ ...createFeatureForm, is_base: checked })}
                />
                <Label>Base feature</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={createFeatureForm.is_active !== false}
                  onCheckedChange={(checked) => setCreateFeatureForm({ ...createFeatureForm, is_active: checked })}
                />
                <Label>Active</Label>
              </div>
              <div className="space-y-2">
                <Label>Sort Order</Label>
                <Input
                  type="number"
                  value={createFeatureForm.sort_order}
                  onChange={(e) => setCreateFeatureForm({ ...createFeatureForm, sort_order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateFeatureDialog(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={() => featuresServiceId && handleCreateFeature(featuresServiceId)}
              disabled={isSaving || !createFeatureForm.feature_key || !createFeatureForm.display_name}
            >
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Feature
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Feature Dialog */}
      <Dialog open={!!editingFeature} onOpenChange={(open) => !open && setEditingFeature(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Feature</DialogTitle>
            <DialogDescription>
              Update feature details and pricing.
            </DialogDescription>
          </DialogHeader>
          {editingFeature && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Feature Key</Label>
                  <Input value={editingFeature.feature_key} disabled className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>Display Name</Label>
                  <Input
                    value={editingFeature.display_name}
                    onChange={(e) => setEditingFeature({ ...editingFeature, display_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingFeature.description || ""}
                  onChange={(e) => setEditingFeature({ ...editingFeature, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>SKU Code</Label>
                  <Input
                    value={editingFeature.sku_code || ""}
                    onChange={(e) => setEditingFeature({ ...editingFeature, sku_code: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Price ZAR (cents)</Label>
                  <Input
                    type="number"
                    value={editingFeature.price_monthly_zar}
                    onChange={(e) => setEditingFeature({ ...editingFeature, price_monthly_zar: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Price USD (cents)</Label>
                  <Input
                    type="number"
                    value={editingFeature.price_monthly_usd}
                    onChange={(e) => setEditingFeature({ ...editingFeature, price_monthly_usd: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={editingFeature.is_base}
                    onCheckedChange={(checked) => setEditingFeature({ ...editingFeature, is_base: checked })}
                  />
                  <Label>Base feature</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={editingFeature.is_active}
                    onCheckedChange={(checked) => setEditingFeature({ ...editingFeature, is_active: checked })}
                  />
                  <Label>Active</Label>
                </div>
                <div className="space-y-2">
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    value={editingFeature.sort_order}
                    onChange={(e) => setEditingFeature({ ...editingFeature, sort_order: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              {editingFeature.b2c_licence_model_id && (
                <div className="rounded border p-3 bg-muted/50">
                  <p className="text-xs text-muted-foreground">
                    B2C Licence Model ID: {editingFeature.b2c_licence_model_id}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingFeature(null)} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={() => editingFeature && handleSaveFeature(editingFeature)}
              disabled={isSaving}
            >
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
